import { useTranslation } from '@/shared/web/i18n/LanguageProvider';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, ChevronLeft, MessageCircle, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@shared/web/api-client';
import type { StaffTicket, StaffTicketsPayload } from '@shared/web/types';
import { useAuth } from '@/features/login/ux/hooks/useAuth';
import { usePermissions } from '@/web/hooks/usePermissions';
import { Button } from '@/web/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/web/components/ui/dropdown-menu';
import { cn } from '@/web/lib/utils';
import { supportActivity, SUPPORT_IDLE_MS } from './support-activity';
import { hasUnreadSupportMessage, markSupportTicketRead, SUPPORT_READ_EVENT } from './support-unread';

export function StaffSupportDock() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const allowed = Boolean(user && /^\/(pipeline|revenue|videos|settings|inbound|support|applications|admin\/employee-onboarding)(\/|$)/.test(location.pathname) && can('support.view'));
  const canManage = can('support.manage');
  const [tickets, setTickets] = useState<StaffTicket[]>([]);
  const [teammates, setTeammates] = useState<{ id: string; name: string }[]>([]);
  const [selected, setSelected] = useState<StaffTicket | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [, setReadRevision] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );
  const lastHeartbeat = useRef(0);
  const selectedId = useRef<string | null>(null);
  const seen = useRef(new Map<string, string>());
  const observed = useRef(new Set<string>());
  const owners = useRef(new Map<string, string | null>());
  const initialized = useRef(false);
  const refreshing = useRef(false);
  const sessionUserId = useRef(user?.id);
  if (sessionUserId.current !== user?.id) {
    sessionUserId.current = user?.id;
    supportActivity.confirmedAt = 0;
    initialized.current = false;
    seen.current.clear();
    observed.current.clear();
    owners.current.clear();
    selectedId.current = null;
  }

  useEffect(() => {
    if (!allowed) return;
    const refresh = () => setReadRevision((value) => value + 1);
    window.addEventListener(SUPPORT_READ_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(SUPPORT_READ_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [allowed]);

  useEffect(() => {
    if (!allowed || !expanded || !selected || !user) return;
    const read = () => {
      if (document.visibilityState === 'visible') markSupportTicketRead(selected, user.id);
    };
    read();
    document.addEventListener('visibilitychange', read);
    return () => document.removeEventListener('visibilitychange', read);
  }, [allowed, expanded, selected, user?.id]);

  useEffect(() => {
    if (!allowed || !canManage || typeof Notification === 'undefined') return;
    const refreshPermission = () => setNotificationPermission(Notification.permission);
    window.addEventListener('focus', refreshPermission);
    return () => window.removeEventListener('focus', refreshPermission);
  }, [allowed, canManage]);

  useEffect(() => {
    if (!allowed || !canManage) return;
    const activity = () => {
      if (document.visibilityState !== 'visible') return;
      supportActivity.lastAt = Date.now();
      void heartbeat();
    };
    const heartbeat = async () => {
      if (document.visibilityState !== 'visible' || Date.now() - supportActivity.lastAt >= SUPPORT_IDLE_MS) return;
      if (Date.now() - lastHeartbeat.current < 30_000) return;
      lastHeartbeat.current = Date.now();
      try {
        await api.post('/api/support/presence');
        supportActivity.confirmedAt = Date.now();
      } catch { lastHeartbeat.current = 0; supportActivity.confirmedAt = 0; }
    };
    const tick = () => {
      if (document.visibilityState !== 'visible') lastHeartbeat.current = 0;
      void heartbeat();
    };
    window.addEventListener('pointerdown', activity);
    window.addEventListener('keydown', activity);
    window.addEventListener('focus', activity);
    document.addEventListener('visibilitychange', tick);
    const interval = window.setInterval(tick, 15_000);
    tick();
    return () => {
      window.removeEventListener('pointerdown', activity);
      window.removeEventListener('keydown', activity);
      window.removeEventListener('focus', activity);
      document.removeEventListener('visibilitychange', tick);
      window.clearInterval(interval);
    };
  }, [allowed, canManage]);

  useEffect(() => {
    if (!allowed || !canManage) return;
    void api.get<{ teammates: { id: string; name: string }[] }>('/api/support/teammates')
      .then((result) => setTeammates(result.teammates))
      .catch(() => {});
  }, [allowed, canManage]);

  useEffect(() => {
    if (!allowed) return;
    let disposed = false;
    const showDesktopNotification = (ticket: StaffTicket) => {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
      try {
        const notification = new Notification('New creator support ticket', {
          body: 'A creator needs support. Open the dashboard to read their message.',
          tag: `support-ticket-${ticket.id}`,
        });
        notification.onclick = () => {
          window.focus();
          selectedId.current = ticket.id;
          setSelected(null);
          setBody('');
          setExpanded(true);
          void openTicket(ticket.id);
          notification.close();
        };
        return true;
      } catch {
        return false;
      }
    };
    const refresh = async () => {
      if (refreshing.current) return;
      refreshing.current = true;
      try {
        const result = await api.get<StaffTicketsPayload>('/api/support/tickets');
        if (disposed) return;
        if (initialized.current && canManage) {
          for (const ticket of result.tickets) {
            const previous = seen.current.get(ticket.id);
            const newTicket = !observed.current.has(ticket.id);
            const transferredToMe = owners.current.has(ticket.id) && owners.current.get(ticket.id) !== user?.id && ticket.assignee?.id === user?.id;
            if (ticket.awaiting === 'agency' && ticket.lastCreatorMessageAt &&
                (!ticket.assignee || ticket.assignee.id === user?.id)) {
              const desktopNotified = newTicket && !ticket.assignee && showDesktopNotification(ticket);
              if (desktopNotified && document.visibilityState !== 'visible') {
                seen.current.set(ticket.id, ticket.lastCreatorMessageAt);
              }
              if ((!desktopNotified || !newTicket) && (previous === undefined || transferredToMe || previous !== ticket.lastCreatorMessageAt) &&
                  document.visibilityState === 'visible' && Date.now() - supportActivity.lastAt < SUPPORT_IDLE_MS) {
                toast('New creator message', {
                  description: `${ticket.creator?.name || ticket.creator?.handle || 'Creator'}: ${ticket.subject}`,
                  action: { label: 'Open chat', onClick: () => { selectedId.current = ticket.id; setSelected(null); setBody(''); setExpanded(true); void openTicket(ticket.id); } },
                });
              }
            }
          }
        }
        initialized.current = true;
        observed.current = new Set(result.tickets.map((ticket) => ticket.id));
        if (document.visibilityState === 'visible' && Date.now() - supportActivity.lastAt < SUPPORT_IDLE_MS) {
          seen.current = new Map(result.tickets.map((ticket) => [ticket.id, ticket.lastCreatorMessageAt ?? '']));
          owners.current = new Map(result.tickets.map((ticket) => [ticket.id, ticket.assignee?.id ?? null]));
        }
        setTickets(result.tickets);
        if (selectedId.current) void openTicket(selectedId.current);
      } catch {
        // The next event or timer retries without spamming the current screen.
      } finally { refreshing.current = false; }
    };
    const openTicket = async (id: string) => {
      try {
        const { ticket } = await api.get<{ ticket: StaffTicket }>(`/api/support/tickets/${id}`);
        if (!disposed && selectedId.current === id) setSelected(ticket);
      } catch { /* A stale selection does not block the queue. */ }
    };
    const source = new EventSource('/api/support/events');
    source.addEventListener('change', () => void refresh());
    source.addEventListener('open', () => void refresh());
    const onFocus = () => void refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); }, 15_000);
    void refresh();
    return () => { disposed = true; refreshing.current = false; source.close(); window.clearInterval(timer); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVisible); };
  }, [allowed, canManage, user?.id]);

  const select = async (id: string) => {
    selectedId.current = id;
    setSelected(null);
    setBody('');
    setExpanded(true);
    try {
      const { ticket } = await api.get<{ ticket: StaffTicket }>(`/api/support/tickets/${id}`);
      if (selectedId.current === id) setSelected(ticket);
    } catch { toast.error('Could not load that conversation'); }
  };
  const join = async () => {
    if (!selected) return;
    try {
      const { ticket } = await api.post<{ ticket: StaffTicket }>(`/api/support/tickets/${selected.id}/join`);
      if (selectedId.current === ticket.id) setSelected(ticket);
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not join'); }
  };
  const resolve = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      const { ticket } = await api.patch<{ ticket: StaffTicket }>(`/api/support/tickets/${selected.id}`, { status: 'resolved' });
      if (selectedId.current === ticket.id) setSelected(ticket);
      toast.success('Ticket resolved');
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not resolve ticket'); }
    finally { setSending(false); }
  };
  const transfer = async (assignedUserId: string) => {
    if (!selected || !assignedUserId || assignedUserId === selected.assignee?.id) return;
    setTransferring(true);
    try {
      const { ticket } = await api.patch<{ ticket: StaffTicket }>(`/api/support/tickets/${selected.id}`, { assignedUserId });
      if (selectedId.current === ticket.id) setSelected(ticket);
      toast.success('Ticket transferred');
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not transfer ticket'); }
    finally { setTransferring(false); }
  };
  const reply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || selectedId.current !== selected.id || !body.trim() || sending) return;
    setSending(true);
    try {
      const { ticket } = await api.post<{ ticket: StaffTicket }>(`/api/support/tickets/${selected.id}/reply`, { body: body.trim() });
      if (selectedId.current === ticket.id) {
        setSelected(ticket);
        setBody('');
      }
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not send'); }
    finally { setSending(false); }
  };

  const enableDesktopNotifications = async () => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'denied') {
      toast.error('Desktop notifications are blocked. Allow them in your browser site settings.');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') toast.success('Desktop support alerts enabled');
    } catch {
      toast.error('Desktop notifications are unavailable in this browser.');
    }
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (body.trim() && !sending) event.currentTarget.form?.requestSubmit();
  };

  if (!allowed) return null;
  const openTickets = tickets.filter((ticket) => ticket.status !== 'resolved');
  const hasUnread = Boolean(user && openTickets.some((ticket) => hasUnreadSupportMessage(ticket, user.id)));
  return (
    <>
      <div className="fixed right-2 bottom-2 z-40 flex flex-col items-end gap-3 sm:right-6 sm:bottom-6">
        {expanded && (
          <section aria-label={t('Creator chat')} className="bg-card border-border flex h-[min(36rem,calc(100dvh-7rem))] w-[min(25rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-2xl border shadow-2xl">
            <header className="bg-primary text-primary-foreground flex items-center justify-between px-4 py-3">
              <div><p className="text-sm font-semibold">Creator support</p><p className="text-xs opacity-75">All open tickets, across the team</p></div>
              <div className="flex items-center gap-1">
                {canManage && notificationPermission !== 'granted' && notificationPermission !== 'unsupported' && (
                  <Button type="button" size="sm" variant="ghost" className="text-xs" onClick={() => void enableDesktopNotifications()}><Bell className="size-4" /> Enable desktop alerts</Button>
                )}
                <Button size="icon" variant="ghost" aria-label={t('Close chat')} onClick={() => setExpanded(false)}><X className="size-4" /></Button>
              </div>
            </header>
            {selected ? (
              <>
                <div className="border-border shrink-0 border-b px-2 py-1">
                  <Button type="button" variant="ghost" size="sm" onClick={() => { selectedId.current = null; setSelected(null); setBody(''); }}>
                    <ChevronLeft data-icon="inline-start" /> Back to queue
                  </Button>
                </div>
                <div className="border-border flex items-center gap-2 border-b px-4 py-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{selected.subject}</span>
                  {canManage && selected.status !== 'resolved' && <Button size="sm" variant="ghost" className="ml-auto shrink-0" disabled={sending} onClick={() => void resolve()}>Resolve</Button>}
                  {canManage && selected.status !== 'resolved' && teammates.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="min-w-0 max-w-32"
                          aria-label={t('Transfer ticket to teammate, currently {name}', { name: selected.assignee?.name || t('Unassigned') })}
                          disabled={transferring}
                        >
                          <span className="truncate">{selected.assignee?.name || t('Unassigned')}</span>
                          <ChevronDown data-icon="inline-end" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuGroup>
                          <DropdownMenuRadioGroup value={selected.assignee?.id ?? ''} onValueChange={(value) => void transfer(value)}>
                            {teammates.map((member) => (
                              <DropdownMenuRadioItem key={member.id} value={member.id}>{member.name}</DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className={cn('text-muted-foreground shrink-0 text-xs', selected.status === 'resolved' && 'ml-auto')}>{selected.assignee?.name || t('Unassigned')}</span>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
                  {selected.messages?.map((message) => (
                    <div key={message.id} className={cn('max-w-[88%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap', message.isInternal ? 'border border-dashed border-amber-500/50 bg-amber-500/5' : message.authorRole === 'agency' ? 'bg-primary text-primary-foreground self-end' : 'bg-secondary self-start')}>
                      <p className="mb-1 text-[11px] opacity-70">{message.isInternal ? 'Internal note' : message.authorRole === 'agency' ? message.authorName || 'A2' : selected.creator?.name || t("Creator")}</p>{message.body}
                    </div>
                  ))}
                </div>
                {selected.feedback && (
                  <div className="border-border bg-secondary/30 mx-3 mb-2 rounded-lg border px-3 py-2 text-xs">
                    Creator rated this support {selected.feedback.rating === 'up' ? 'helpful' : 'not helpful'}.
                    {selected.feedback.comment && <p className="text-muted-foreground mt-1 whitespace-pre-wrap">{selected.feedback.comment}</p>}
                  </div>
                )}
                {canManage && !selected.assignee && selected.status !== 'resolved' && <Button className="mx-4 mb-2" variant="secondary" onClick={() => void join()}>Join chat</Button>}
                {canManage && selected.status !== 'resolved' && (
                  <form onSubmit={reply} className="border-border border-t p-3">
                    <div className="border-input bg-background focus-within:border-ring focus-within:ring-ring/40 flex items-end gap-2 rounded-2xl border p-1.5 pl-3 shadow-xs focus-within:ring-2">
                      <textarea
                        aria-label={t('Reply to creator')}
                        value={body}
                        onChange={(event) => setBody(event.target.value)}
                        onKeyDown={onComposerKeyDown}
                        rows={1}
                        placeholder={t('Reply to creator…')}
                        className="field-sizing-content max-h-32 min-h-9 min-w-0 flex-1 resize-none bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
                      />
                      <Button size="icon" type="submit" className="size-8 shrink-0 rounded-full" disabled={!body.trim() || sending} aria-label={t('Send reply')}><Send className="size-4" /></Button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <div className="flex flex-1 flex-col overflow-y-auto">
                <p className="text-muted-foreground px-4 pt-4 pb-2 text-xs font-medium uppercase tracking-wider">All open tickets</p>
                {openTickets.length === 0 && <p className="text-muted-foreground px-4 py-6 text-sm">No open tickets.</p>}
                {openTickets.map((ticket) => (
                  <button type="button" key={ticket.id} onClick={() => void select(ticket.id)} className="border-border hover:bg-secondary/50 flex flex-col gap-1 border-b px-4 py-3 text-left">
                    <span className="flex w-full items-center gap-2 text-sm font-medium">
                      <span className="truncate">{ticket.subject}</span>
                      {user && hasUnreadSupportMessage(ticket, user.id) && <span aria-label={t('Unread messages')} className="bg-destructive size-2 shrink-0 rounded-full" />}
                    </span>
                    <span className="text-muted-foreground text-xs">{ticket.creator?.name || ticket.creator?.handle || t('Creator')}</span>
                    <span className="text-muted-foreground text-xs">
                      {ticket.assignee ? `Assigned to ${ticket.assignee.name}` : t("Unassigned")}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <button type="button" className="border-border text-muted-foreground hover:text-foreground border-t px-4 py-3 text-center text-xs" onClick={() => { setExpanded(false); navigate('/support'); }}>Open full support queue</button>
          </section>
        )}
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="relative size-16 rounded-full border shadow-lg [&_svg:not([class*='size-'])]:size-6"
          onClick={() => setExpanded(!expanded)}
          aria-label={hasUnread
            ? expanded ? t('Close creator support, unread messages') : t('Open creator support, unread messages')
            : expanded ? t('Close creator support') : t('Open creator support')}
          aria-expanded={expanded}
        >
          {expanded ? <X /> : <MessageCircle />}
          {hasUnread && (
            <span aria-hidden="true" className="pointer-events-none absolute top-1 right-1 size-4">
              <span className="bg-destructive absolute inset-0 rounded-full opacity-75 motion-safe:animate-ping" />
              <span className="bg-destructive ring-background absolute inset-0 rounded-full ring-2" />
            </span>
          )}
        </Button>
      </div>
    </>
  );
}
